import { z } from "zod";

/**
 * Application roles. These are Compliance Sage roles and are intentionally
 * independent of any VaultSage account/team role. VaultSage is used only for
 * authentication (identity) and organization-owned file storage.
 */
export const Role = {
  SUPER_ADMIN: "SUPER_ADMIN",
  ADMIN: "ADMIN",
  USER: "USER",
} as const;
export const roleSchema = z.enum([Role.SUPER_ADMIN, Role.ADMIN, Role.USER]);
export type Role = z.infer<typeof roleSchema>;

/** Platform authorization is independent of organization memberships. */
export const PlatformRole = {
  SUPER_ADMIN: "SUPER_ADMIN",
  USER: "USER",
} as const;
export const platformRoleSchema = z.enum([PlatformRole.SUPER_ADMIN, PlatformRole.USER]);
export type PlatformRole = z.infer<typeof platformRoleSchema>;

export const VaultSageCredentialKind = {
  PERSONAL_API_KEY: "PERSONAL_API_KEY",
  TEAM_API_KEY: "TEAM_API_KEY",
} as const;
export const vaultSageCredentialKindSchema = z.enum([
  VaultSageCredentialKind.PERSONAL_API_KEY,
  VaultSageCredentialKind.TEAM_API_KEY,
]);
export type VaultSageCredentialKind = z.infer<typeof vaultSageCredentialKindSchema>;

/** Roles that may be stored on an organization membership. */
export const OrganizationRole = {
  ADMIN: "ADMIN",
  USER: "USER",
} as const;
export const organizationRoleSchema = z.enum([OrganizationRole.ADMIN, OrganizationRole.USER]);
export type OrganizationRole = z.infer<typeof organizationRoleSchema>;

/** Membership lifecycle. Managers add an email; it activates on first login. */
export const MembershipStatus = {
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  DISABLED: "DISABLED",
} as const;
export const membershipStatusSchema = z.enum([
  MembershipStatus.PENDING,
  MembershipStatus.ACTIVE,
  MembershipStatus.DISABLED,
]);
export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

/** How authoritative a policy document is. */
export const PolicySource = {
  REGULATION: "REGULATION",
  AUTHORITY_GUIDANCE: "AUTHORITY_GUIDANCE",
  INDUSTRY_RULE: "INDUSTRY_RULE",
  INTERNAL_POLICY: "INTERNAL_POLICY",
  PRODUCT_RULE: "PRODUCT_RULE",
  PROHIBITED_WORDS: "PROHIBITED_WORDS",
  APPROVED_WORDING: "APPROVED_WORDING",
} as const;
export const policySourceSchema = z.enum([
  PolicySource.REGULATION,
  PolicySource.AUTHORITY_GUIDANCE,
  PolicySource.INDUSTRY_RULE,
  PolicySource.INTERNAL_POLICY,
  PolicySource.PRODUCT_RULE,
  PolicySource.PROHIBITED_WORDS,
  PolicySource.APPROVED_WORDING,
]);
export type PolicySource = z.infer<typeof policySourceSchema>;

/** Approval lifecycle shared by policy versions and rule versions. */
export const ApprovalStatus = {
  DRAFT: "DRAFT",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  RETIRED: "RETIRED",
} as const;
export const approvalStatusSchema = z.enum([
  ApprovalStatus.DRAFT,
  ApprovalStatus.PENDING_APPROVAL,
  ApprovalStatus.APPROVED,
  ApprovalStatus.RETIRED,
]);
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

/** VaultSage file indexing/processing status mirrored locally. */
export const ProcessingStatus = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  READY: "READY",
  FAILED: "FAILED",
} as const;
export const processingStatusSchema = z.enum([
  ProcessingStatus.PENDING,
  ProcessingStatus.PROCESSING,
  ProcessingStatus.READY,
  ProcessingStatus.FAILED,
]);
export type ProcessingStatus = z.infer<typeof processingStatusSchema>;

/** Lifecycle of the browser-readable PDF derived from a policy source file. */
export const DocumentNormalizationStatus = {
  NOT_REQUIRED: "NOT_REQUIRED",
  UNPROCESSED: "UNPROCESSED",
  PROCESSING: "PROCESSING",
  READY: "READY",
  FAILED: "FAILED",
} as const;
export const documentNormalizationStatusSchema = z.enum([
  DocumentNormalizationStatus.NOT_REQUIRED,
  DocumentNormalizationStatus.UNPROCESSED,
  DocumentNormalizationStatus.PROCESSING,
  DocumentNormalizationStatus.READY,
  DocumentNormalizationStatus.FAILED,
]);
export type DocumentNormalizationStatus = z.infer<typeof documentNormalizationStatusSchema>;

/** Category of a custom rule; also drives which deterministic check runs. */
export const RuleCategory = {
  REQUIRED_DISCLAIMER: "REQUIRED_DISCLAIMER",
  PROHIBITED_EXPRESSION: "PROHIBITED_EXPRESSION",
  REQUIRED_FIELD: "REQUIRED_FIELD",
  COMPARISON_CLAIM: "COMPARISON_CLAIM",
  NUMERIC_CLAIM: "NUMERIC_CLAIM",
  VISUAL_REFERENCE: "VISUAL_REFERENCE",
  GENERAL: "GENERAL",
} as const;
export const ruleCategorySchema = z.enum([
  RuleCategory.REQUIRED_DISCLAIMER,
  RuleCategory.PROHIBITED_EXPRESSION,
  RuleCategory.REQUIRED_FIELD,
  RuleCategory.COMPARISON_CLAIM,
  RuleCategory.NUMERIC_CLAIM,
  RuleCategory.VISUAL_REFERENCE,
  RuleCategory.GENERAL,
]);
export type RuleCategory = z.infer<typeof ruleCategorySchema>;

/**
 * MANUAL rules are authored directly via the rule form. TAUGHT rules are
 * drafted by AI from an "Educate Sage" lesson (a real before/feedback/after
 * case) and are shown to users as a plain-language lesson card instead of the
 * technical rule form.
 */
export const RuleOrigin = {
  MANUAL: "MANUAL",
  TAUGHT: "TAUGHT",
} as const;
export const ruleOriginSchema = z.enum([RuleOrigin.MANUAL, RuleOrigin.TAUGHT]);
export type RuleOrigin = z.infer<typeof ruleOriginSchema>;

/**
 * STANDARD reviews are real submissions. KNOWLEDGE_TEST reviews are an
 * admin's "try it" self-test of a single Educate Sage lesson against a
 * sample material; they are excluded from the normal review queue/dashboard.
 */
export const ReviewType = {
  STANDARD: "STANDARD",
  KNOWLEDGE_TEST: "KNOWLEDGE_TEST",
} as const;
export const reviewTypeSchema = z.enum([ReviewType.STANDARD, ReviewType.KNOWLEDGE_TEST]);
export type ReviewType = z.infer<typeof reviewTypeSchema>;

/**
 * Language the AI writes its analysis prose in, captured from the requester's
 * UI language when the review is submitted. It only affects generated text —
 * the guides, policies and statutes the analysis cites stay Korean.
 */
export const OutputLanguage = {
  KO: "KO",
  EN: "EN",
} as const;
export const outputLanguageSchema = z.enum([OutputLanguage.KO, OutputLanguage.EN]);
export type OutputLanguage = z.infer<typeof outputLanguageSchema>;

/** Finding severity. */
export const Severity = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL",
} as const;
export const severitySchema = z.enum([
  Severity.LOW,
  Severity.MEDIUM,
  Severity.HIGH,
  Severity.CRITICAL,
]);
export type Severity = z.infer<typeof severitySchema>;

/**
 * Which published guide a required-disclosure rule comes from (the guide's 구분
 * column). This is descriptive provenance, not authority: an organization may
 * enforce a 공통 표기 item exactly as strictly as an association guideline.
 */
export const GuideSource = {
  ASSOCIATION_GUIDELINE: "ASSOCIATION_GUIDELINE",
  COMMON_PRACTICE: "COMMON_PRACTICE",
  OTHER: "OTHER",
} as const;
export const guideSourceSchema = z.enum([
  GuideSource.ASSOCIATION_GUIDELINE,
  GuideSource.COMMON_PRACTICE,
  GuideSource.OTHER,
]);
export type GuideSource = z.infer<typeof guideSourceSchema>;

/**
 * 고정문구 여부. `FIXED` wording must appear verbatim, so it can be checked
 * deterministically; `PER_PRODUCT` wording varies and is advisory to the AI.
 */
export const WordingType = {
  FIXED: "FIXED",
  PER_PRODUCT: "PER_PRODUCT",
} as const;
export const wordingTypeSchema = z.enum([WordingType.FIXED, WordingType.PER_PRODUCT]);
export type WordingType = z.infer<typeof wordingTypeSchema>;

/** 금지 표현 사전 분류. */
export const ForbiddenCategory = {
  EXAGGERATION: "EXAGGERATION",
  MISLEADING: "MISLEADING",
  ONE_SIDED: "ONE_SIDED",
  PROVOCATIVE: "PROVOCATIVE",
  DISPARAGEMENT: "DISPARAGEMENT",
  PROHIBITED_WORDING: "PROHIBITED_WORDING",
  SIGNUP_INDUCEMENT: "SIGNUP_INDUCEMENT",
  TECHNICAL_TERM: "TECHNICAL_TERM",
  INCORRECT_SOURCE: "INCORRECT_SOURCE",
  CUSTOMER_INDUCEMENT: "CUSTOMER_INDUCEMENT",
  OTHER: "OTHER",
} as const;
export const forbiddenCategorySchema = z.enum([
  ForbiddenCategory.EXAGGERATION,
  ForbiddenCategory.MISLEADING,
  ForbiddenCategory.ONE_SIDED,
  ForbiddenCategory.PROVOCATIVE,
  ForbiddenCategory.DISPARAGEMENT,
  ForbiddenCategory.PROHIBITED_WORDING,
  ForbiddenCategory.SIGNUP_INDUCEMENT,
  ForbiddenCategory.TECHNICAL_TERM,
  ForbiddenCategory.INCORRECT_SOURCE,
  ForbiddenCategory.CUSTOMER_INDUCEMENT,
  ForbiddenCategory.OTHER,
]);
export type ForbiddenCategory = z.infer<typeof forbiddenCategorySchema>;

/** How a dictionary entry is matched against advertising copy. */
export const ForbiddenMatchType = {
  SUBSTRING: "SUBSTRING",
  REGEX: "REGEX",
} as const;
export const forbiddenMatchTypeSchema = z.enum([
  ForbiddenMatchType.SUBSTRING,
  ForbiddenMatchType.REGEX,
]);
export type ForbiddenMatchType = z.infer<typeof forbiddenMatchTypeSchema>;

/** Operational outcome shown to an employee after risk routing. */
export const RoutingOutcome = {
  SELF_CHECK_CLEAR: "SELF_CHECK_CLEAR",
  SELF_CHECK_REJECTED: "SELF_CHECK_REJECTED",
  ACTION_REQUIRED: "ACTION_REQUIRED",
  ESCALATED: "ESCALATED",
  PROCESSING_FAILED: "PROCESSING_FAILED",
} as const;
export const routingOutcomeSchema = z.enum([
  RoutingOutcome.SELF_CHECK_CLEAR,
  RoutingOutcome.SELF_CHECK_REJECTED,
  RoutingOutcome.ACTION_REQUIRED,
  RoutingOutcome.ESCALATED,
  RoutingOutcome.PROCESSING_FAILED,
]);
export type RoutingOutcome = z.infer<typeof routingOutcomeSchema>;

export const ReviewPriority = {
  LOW: "LOW",
  NORMAL: "NORMAL",
  HIGH: "HIGH",
  URGENT: "URGENT",
} as const;
export const reviewPrioritySchema = z.enum([
  ReviewPriority.LOW,
  ReviewPriority.NORMAL,
  ReviewPriority.HIGH,
  ReviewPriority.URGENT,
]);
export type ReviewPriority = z.infer<typeof reviewPrioritySchema>;

export const ReviewJobStatus = {
  PENDING: "PENDING",
  RUNNING: "RUNNING",
  RETRYING: "RETRYING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;
export const reviewJobStatusSchema = z.enum([
  ReviewJobStatus.PENDING,
  ReviewJobStatus.RUNNING,
  ReviewJobStatus.RETRYING,
  ReviewJobStatus.COMPLETED,
  ReviewJobStatus.FAILED,
]);
export type ReviewJobStatus = z.infer<typeof reviewJobStatusSchema>;

/** Durable, user-visible stage of a compliance analysis job. */
export const ReviewProgressStage = {
  QUEUED: "QUEUED",
  INDEXING: "INDEXING",
  PREPARING: "PREPARING",
  DETERMINISTIC_CHECKS: "DETERMINISTIC_CHECKS",
  AI_REVIEW: "AI_REVIEW",
  FINALIZING: "FINALIZING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;
export const reviewProgressStageSchema = z.enum([
  ReviewProgressStage.QUEUED,
  ReviewProgressStage.INDEXING,
  ReviewProgressStage.PREPARING,
  ReviewProgressStage.DETERMINISTIC_CHECKS,
  ReviewProgressStage.AI_REVIEW,
  ReviewProgressStage.FINALIZING,
  ReviewProgressStage.COMPLETED,
  ReviewProgressStage.FAILED,
]);
export type ReviewProgressStage = z.infer<typeof reviewProgressStageSchema>;

/** Review workflow lifecycle. */
export const ReviewStatus = {
  DRAFT: "DRAFT",
  SUBMITTED: "SUBMITTED",
  ANALYZING: "ANALYZING",
  AWAITING_DECISION: "AWAITING_DECISION",
  SELF_CHECK_COMPLETE: "SELF_CHECK_COMPLETE",
  SELF_CHECK_REJECTED: "SELF_CHECK_REJECTED",
  DECIDED: "DECIDED",
} as const;
export const reviewStatusSchema = z.enum([
  ReviewStatus.DRAFT,
  ReviewStatus.SUBMITTED,
  ReviewStatus.ANALYZING,
  ReviewStatus.AWAITING_DECISION,
  ReviewStatus.SELF_CHECK_COMPLETE,
  ReviewStatus.SELF_CHECK_REJECTED,
  ReviewStatus.DECIDED,
]);
export type ReviewStatus = z.infer<typeof reviewStatusSchema>;

/**
 * Overall AI risk assessment. This is NEVER a legal compliance determination;
 * only a human decision is authoritative. Low-confidence or failed analysis
 * must resolve to REVIEW_REQUIRED, never PASS.
 */
export const RiskStatus = {
  PASS: "PASS",
  REVIEW_REQUIRED: "REVIEW_REQUIRED",
  HIGH_RISK: "HIGH_RISK",
} as const;
export const riskStatusSchema = z.enum([
  RiskStatus.PASS,
  RiskStatus.REVIEW_REQUIRED,
  RiskStatus.HIGH_RISK,
]);
export type RiskStatus = z.infer<typeof riskStatusSchema>;

/** Analysis run lifecycle. */
export const AnalysisStatus = {
  PENDING: "PENDING",
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;
export const analysisStatusSchema = z.enum([
  AnalysisStatus.PENDING,
  AnalysisStatus.RUNNING,
  AnalysisStatus.COMPLETED,
  AnalysisStatus.FAILED,
]);
export type AnalysisStatus = z.infer<typeof analysisStatusSchema>;

/** Human decision outcomes. */
export const Decision = {
  APPROVED: "APPROVED",
  APPROVED_WITH_CHANGES: "APPROVED_WITH_CHANGES",
  REJECTED: "REJECTED",
} as const;
export const decisionSchema = z.enum([
  Decision.APPROVED,
  Decision.APPROVED_WITH_CHANGES,
  Decision.REJECTED,
]);
export type Decision = z.infer<typeof decisionSchema>;

/**
 * What a finding cites as its authoritative source.
 *
 * `BASELINE` is a reference shipped with the product rather than a document the
 * organization approved. `DICTIONARY` is an entry in the organization's
 * forbidden expression dictionary. `NONE` is assigned by the server when the AI
 * reports an issue that no available reference covers; such a finding is
 * advisory only.
 */
export const CitationType = {
  RULE: "RULE",
  POLICY: "POLICY",
  BASELINE: "BASELINE",
  DICTIONARY: "DICTIONARY",
  NONE: "NONE",
} as const;
export const citationTypeSchema = z.enum([
  CitationType.RULE,
  CitationType.POLICY,
  CitationType.BASELINE,
  CitationType.DICTIONARY,
  CitationType.NONE,
]);
export type CitationType = z.infer<typeof citationTypeSchema>;

/**
 * The subset a model may cite. `NONE` is never accepted from model output; the
 * server assigns it when a finding arrives with no citations at all.
 */
export const aiCitationTypeSchema = z.enum([
  CitationType.RULE,
  CitationType.POLICY,
  CitationType.BASELINE,
  CitationType.DICTIONARY,
]);
export type AiCitationType = z.infer<typeof aiCitationTypeSchema>;

/**
 * Whether a finding's evidence is a verbatim text excerpt or a description of
 * something visible in an image, PDF, or slide. Visual evidence cannot be
 * verified against the submitted text and must not be checked that way.
 */
export const EvidenceType = {
  TEXT: "TEXT",
  VISUAL: "VISUAL",
} as const;
export const evidenceTypeSchema = z.enum([EvidenceType.TEXT, EvidenceType.VISUAL]);
export type EvidenceType = z.infer<typeof evidenceTypeSchema>;

/** Per-reference verdict in the review checklist. */
export const CheckVerdict = {
  OK: "OK",
  ISSUE: "ISSUE",
  NOT_APPLICABLE: "NOT_APPLICABLE",
  CANNOT_VERIFY: "CANNOT_VERIFY",
} as const;
export const checkVerdictSchema = z.enum([
  CheckVerdict.OK,
  CheckVerdict.ISSUE,
  CheckVerdict.NOT_APPLICABLE,
  CheckVerdict.CANNOT_VERIFY,
]);
export type CheckVerdict = z.infer<typeof checkVerdictSchema>;

/** How a finding was detected. */
export const DetectionMethod = {
  DETERMINISTIC: "DETERMINISTIC",
  AI: "AI",
} as const;
export const detectionMethodSchema = z.enum([
  DetectionMethod.DETERMINISTIC,
  DetectionMethod.AI,
]);
export type DetectionMethod = z.infer<typeof detectionMethodSchema>;

/** Confidence attached to an AI finding. */
export const ConfidenceLevel = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
} as const;
export const confidenceLevelSchema = z.enum([
  ConfidenceLevel.LOW,
  ConfidenceLevel.MEDIUM,
  ConfidenceLevel.HIGH,
]);
export type ConfidenceLevel = z.infer<typeof confidenceLevelSchema>;

/**
 * What a reviewer concluded about a finding. This is the ground truth the AI
 * quality program is measured against: `reviewerNote` already lets a reviewer
 * disagree in prose, but prose cannot be counted, so a disagreement recorded
 * only there teaches the organization nothing.
 *  - CONFIRMED: the finding was right and the material had to change.
 *  - FALSE_FLAG: the finding was wrong. Every false flag is one lost automatic
 *    clearance, because a PASS requires zero findings — which is why this is
 *    the primary lever on how much the system can decide by itself.
 *  - NOT_APPLICABLE: the requirement is real but does not govern this material,
 *    which points at rule targeting rather than at the finding itself.
 */
export const FindingDisposition = {
  CONFIRMED: "CONFIRMED",
  FALSE_FLAG: "FALSE_FLAG",
  NOT_APPLICABLE: "NOT_APPLICABLE",
} as const;
export const findingDispositionSchema = z.enum([
  FindingDisposition.CONFIRMED,
  FindingDisposition.FALSE_FLAG,
  FindingDisposition.NOT_APPLICABLE,
]);
export type FindingDisposition = z.infer<typeof findingDispositionSchema>;

/**
 * Whether a channel × category combination has earned automatic clearance.
 * Derived from observed agreement, never set by hand: the point is that an
 * administrator enables automation because the evidence supports it, not
 * because the checkbox was available.
 */
export const AutoClearReadiness = {
  /** Too few human decisions on the current engine lineage to say anything. */
  INSUFFICIENT_DATA: "INSUFFICIENT_DATA",
  /** Enough decisions, but the false-clear bound is still above the bar. */
  NOT_READY: "NOT_READY",
  /** Meets the bar and is not yet switched on. */
  READY: "READY",
  /** Switched on and still meeting the bar. */
  ACTIVE: "ACTIVE",
  /** A clear result was overturned by a reviewer. Turn it off and teach. */
  SUSPENDED: "SUSPENDED",
} as const;
export const autoClearReadinessSchema = z.enum([
  AutoClearReadiness.INSUFFICIENT_DATA,
  AutoClearReadiness.NOT_READY,
  AutoClearReadiness.READY,
  AutoClearReadiness.ACTIVE,
  AutoClearReadiness.SUSPENDED,
]);
export type AutoClearReadiness = z.infer<typeof autoClearReadinessSchema>;

/**
 * What a client is reporting. The kind decides which context the server
 * attaches and how the ticket is triaged, so it is a controlled value rather
 * than something the reporter describes in prose.
 */
export const SupportTicketKind = {
  /** The product misbehaved: a failed job, a broken upload, an error screen. */
  SYSTEM_ERROR: "SYSTEM_ERROR",
  /** The compliance outcome is wrong: a bad finding, a miss, or wrong routing. */
  DISPUTED_RESULT: "DISPUTED_RESULT",
  /** Neither: a question about how to use the product. */
  QUESTION: "QUESTION",
} as const;
export const supportTicketKindSchema = z.enum([
  SupportTicketKind.SYSTEM_ERROR,
  SupportTicketKind.DISPUTED_RESULT,
  SupportTicketKind.QUESTION,
]);
export type SupportTicketKind = z.infer<typeof supportTicketKindSchema>;

/** Ticket lifecycle. Only the platform Super Admin moves a ticket forward. */
export const SupportTicketStatus = {
  OPEN: "OPEN",
  ACKNOWLEDGED: "ACKNOWLEDGED",
  IN_PROGRESS: "IN_PROGRESS",
  RESOLVED: "RESOLVED",
  WONT_FIX: "WONT_FIX",
} as const;
export const supportTicketStatusSchema = z.enum([
  SupportTicketStatus.OPEN,
  SupportTicketStatus.ACKNOWLEDGED,
  SupportTicketStatus.IN_PROGRESS,
  SupportTicketStatus.RESOLVED,
  SupportTicketStatus.WONT_FIX,
]);
export type SupportTicketStatus = z.infer<typeof supportTicketStatusSchema>;

/** How badly the reporter is blocked. Drives inbox ordering, not routing. */
export const SupportSeverity = {
  BLOCKING: "BLOCKING",
  DEGRADED: "DEGRADED",
  MINOR: "MINOR",
} as const;
export const supportSeveritySchema = z.enum([
  SupportSeverity.BLOCKING,
  SupportSeverity.DEGRADED,
  SupportSeverity.MINOR,
]);
export type SupportSeverity = z.infer<typeof supportSeveritySchema>;

/** Which side of the conversation a ticket message came from. */
export const SupportAuthorKind = {
  REPORTER: "REPORTER",
  SUPPORT: "SUPPORT",
  /** Written by the platform itself, e.g. an auto-opened failure ticket. */
  SYSTEM: "SYSTEM",
} as const;
export const supportAuthorKindSchema = z.enum([
  SupportAuthorKind.REPORTER,
  SupportAuthorKind.SUPPORT,
  SupportAuthorKind.SYSTEM,
]);
export type SupportAuthorKind = z.infer<typeof supportAuthorKindSchema>;

/** Who wrote a turn in an Ask Sage conversation. */
export const AskSageRole = {
  USER: "USER",
  SAGE: "SAGE",
} as const;
export const askSageRoleSchema = z.enum([AskSageRole.USER, AskSageRole.SAGE]);
export type AskSageRole = z.infer<typeof askSageRoleSchema>;

/**
 * What kind of answer Sage gave. The two refusals are deliberately distinct:
 * NEEDS_REVIEW means the question is a compliance judgement that only a real
 * review can settle, OUT_OF_SCOPE that it is not a question about this product
 * or its standards at all. FAILED covers an AI call that never produced an
 * answer, so an administrator does not read a timeout as a refusal.
 */
export const AskSageAnswerKind = {
  COMPLIANCE: "COMPLIANCE",
  SYSTEM_USAGE: "SYSTEM_USAGE",
  NEEDS_REVIEW: "NEEDS_REVIEW",
  OUT_OF_SCOPE: "OUT_OF_SCOPE",
  FAILED: "FAILED",
} as const;
export const askSageAnswerKindSchema = z.enum([
  AskSageAnswerKind.COMPLIANCE,
  AskSageAnswerKind.SYSTEM_USAGE,
  AskSageAnswerKind.NEEDS_REVIEW,
  AskSageAnswerKind.OUT_OF_SCOPE,
  AskSageAnswerKind.FAILED,
]);
export type AskSageAnswerKind = z.infer<typeof askSageAnswerKindSchema>;

/**
 * Which AI-generated unit a report is anchored to. Stored alongside the reason
 * so the platform can answer "which surface produces reports" — the reason
 * alone cannot tell an AI-summary complaint from a page-level one.
 */
export const SupportReportAnchor = {
  /** The review as a whole, with no narrower unit named. */
  REVIEW: "REVIEW",
  /** The AI summary and its recommended action. */
  AI_SUMMARY: "AI_SUMMARY",
  /** One reference row of the checklist and its verdict. */
  CHECKLIST_ITEM: "CHECKLIST_ITEM",
  /** One finding. */
  FINDING: "FINDING",
  /** The analysis never produced a result. */
  ANALYSIS_FAILURE: "ANALYSIS_FAILURE",
} as const;
export const supportReportAnchorSchema = z.enum([
  SupportReportAnchor.REVIEW,
  SupportReportAnchor.AI_SUMMARY,
  SupportReportAnchor.CHECKLIST_ITEM,
  SupportReportAnchor.FINDING,
  SupportReportAnchor.ANALYSIS_FAILURE,
]);
export type SupportReportAnchor = z.infer<typeof supportReportAnchorSchema>;

/**
 * Why the reporter thinks the result is wrong. A controlled value picked with
 * one click, so a report costs no typing and the inbox can aggregate causes
 * instead of reading prose. Free text stays available as an optional note.
 */
export const SupportReportReason = {
  NOT_A_VIOLATION: "NOT_A_VIOLATION",
  WRONG_CITATION: "WRONG_CITATION",
  WRONG_SEVERITY: "WRONG_SEVERITY",
  MISSED_SOMETHING: "MISSED_SOMETHING",
  EXPLANATION_UNCLEAR: "EXPLANATION_UNCLEAR",
  ANALYSIS_FAILED: "ANALYSIS_FAILED",
  OTHER: "OTHER",
} as const;
export const supportReportReasonSchema = z.enum([
  SupportReportReason.NOT_A_VIOLATION,
  SupportReportReason.WRONG_CITATION,
  SupportReportReason.WRONG_SEVERITY,
  SupportReportReason.MISSED_SOMETHING,
  SupportReportReason.EXPLANATION_UNCLEAR,
  SupportReportReason.ANALYSIS_FAILED,
  SupportReportReason.OTHER,
]);
export type SupportReportReason = z.infer<typeof supportReportReasonSchema>;

/** Append-only audit event categories. */
export const AuditAction = {
  ORGANIZATION_CREATED: "ORGANIZATION_CREATED",
  ORGANIZATION_UPDATED: "ORGANIZATION_UPDATED",
  ORGANIZATION_ARCHIVED: "ORGANIZATION_ARCHIVED",
  ORGANIZATION_SELECTED: "ORGANIZATION_SELECTED",
  VAULTSAGE_CREDENTIAL_CONFIGURED: "VAULTSAGE_CREDENTIAL_CONFIGURED",
  VAULTSAGE_CREDENTIAL_ROTATED: "VAULTSAGE_CREDENTIAL_ROTATED",
  VAULTSAGE_CREDENTIAL_REJECTED: "VAULTSAGE_CREDENTIAL_REJECTED",
  VAULTSAGE_STORAGE_VERIFIED: "VAULTSAGE_STORAGE_VERIFIED",
  VAULTSAGE_ORPHAN_STORAGE_DELETED: "VAULTSAGE_ORPHAN_STORAGE_DELETED",
  LOGIN: "LOGIN",
  LOGOUT: "LOGOUT",
  USER_PROFILE_UPDATED: "USER_PROFILE_UPDATED",
  USER_PLATFORM_ROLE_CHANGED: "USER_PLATFORM_ROLE_CHANGED",
  MEMBER_INVITED: "MEMBER_INVITED",
  MEMBER_ACTIVATED: "MEMBER_ACTIVATED",
  MEMBER_NAME_UPDATED: "MEMBER_NAME_UPDATED",
  MEMBER_DEPARTMENT_UPDATED: "MEMBER_DEPARTMENT_UPDATED",
  MEMBER_ROLE_CHANGED: "MEMBER_ROLE_CHANGED",
  MEMBER_DISABLED: "MEMBER_DISABLED",
  MEMBER_DELETED: "MEMBER_DELETED",
  POLICY_CREATED: "POLICY_CREATED",
  POLICY_RENAMED: "POLICY_RENAMED",
  POLICY_VERSION_UPLOADED: "POLICY_VERSION_UPLOADED",
  POLICY_ACTIVATED: "POLICY_ACTIVATED",
  POLICY_RETIRED: "POLICY_RETIRED",
  POLICY_ARCHIVED: "POLICY_ARCHIVED",
  POLICY_UNARCHIVED: "POLICY_UNARCHIVED",
  POLICY_DELETED: "POLICY_DELETED",
  POLICY_VERSION_DOWNLOADED: "POLICY_VERSION_DOWNLOADED",
  POLICY_VERSION_REPROCESSED: "POLICY_VERSION_REPROCESSED",
  POLICY_SCOPE_UPDATED: "POLICY_SCOPE_UPDATED",
  RULE_CREATED: "RULE_CREATED",
  RULE_VERSION_CREATED: "RULE_VERSION_CREATED",
  RULE_ACTIVATED: "RULE_ACTIVATED",
  RULE_RETIRED: "RULE_RETIRED",
  RULE_DELETED: "RULE_DELETED",
  RULE_REFERENCE_UPLOADED: "RULE_REFERENCE_UPLOADED",
  RULE_REFERENCE_DELETED: "RULE_REFERENCE_DELETED",
  // No longer written (guide matrix removed); kept so existing audit rows still parse.
  RULE_CHANNEL_UPDATED: "RULE_CHANNEL_UPDATED",
  RULE_SCOPE_UPDATED: "RULE_SCOPE_UPDATED",
  LESSON_SUMMARY_UPDATED: "LESSON_SUMMARY_UPDATED",
  CHANNEL_CREATED: "CHANNEL_CREATED",
  CHANNEL_UPDATED: "CHANNEL_UPDATED",
  CHANNEL_DELETED: "CHANNEL_DELETED",
  CATEGORY_CREATED: "CATEGORY_CREATED",
  CATEGORY_UPDATED: "CATEGORY_UPDATED",
  CATEGORY_DELETED: "CATEGORY_DELETED",
  CATEGORY_TEMPLATE_APPLIED: "CATEGORY_TEMPLATE_APPLIED",
  DICTIONARY_ENTRY_CREATED: "DICTIONARY_ENTRY_CREATED",
  DICTIONARY_ENTRY_UPDATED: "DICTIONARY_ENTRY_UPDATED",
  DICTIONARY_ENTRY_DELETED: "DICTIONARY_ENTRY_DELETED",
  DICTIONARY_IMPORTED: "DICTIONARY_IMPORTED",
  // No longer written (synonym management removed); kept so existing audit rows still parse.
  SYNONYM_GROUP_CREATED: "SYNONYM_GROUP_CREATED",
  SYNONYM_GROUP_UPDATED: "SYNONYM_GROUP_UPDATED",
  SYNONYM_GROUP_DELETED: "SYNONYM_GROUP_DELETED",
  BASELINE_GUIDE_APPLIED: "BASELINE_GUIDE_APPLIED",
  CONFIG_UPDATED: "CONFIG_UPDATED",
  REVIEW_CREATED: "REVIEW_CREATED",
  REVIEW_SUBMITTED: "REVIEW_SUBMITTED",
  REVIEW_DELETED: "REVIEW_DELETED",
  REVIEW_ANALYZED: "REVIEW_ANALYZED",
  REVIEW_RESUBMITTED: "REVIEW_RESUBMITTED",
  REVIEW_RERUN: "REVIEW_RERUN",
  REVIEW_ASSIGNED: "REVIEW_ASSIGNED",
  FINDING_NOTE_UPDATED: "FINDING_NOTE_UPDATED",
  FINDING_DISPOSITION_SET: "FINDING_DISPOSITION_SET",
  FINDING_LOCATION_UPDATED: "FINDING_LOCATION_UPDATED",
  MISSED_FINDING_RECORDED: "MISSED_FINDING_RECORDED",
  DECISION_RECORDED: "DECISION_RECORDED",
  REPORT_DOWNLOADED: "REPORT_DOWNLOADED",
  // The approved material itself, with the approval page appended. Separate
  // from REPORT_DOWNLOADED because this is the artifact that leaves the company
  // and gets published, not the internal review record.
  APPROVED_DOCUMENT_DOWNLOADED: "APPROVED_DOCUMENT_DOWNLOADED",
  SUPPORT_TICKET_CREATED: "SUPPORT_TICKET_CREATED",
  SUPPORT_TICKET_STATUS_CHANGED: "SUPPORT_TICKET_STATUS_CHANGED",
  SUPPORT_TICKET_REPLIED: "SUPPORT_TICKET_REPLIED",
  // Cross-tenant reads of client material by the platform. Separate actions so
  // "who looked at this organization's copy" is answerable without parsing
  // metadata, and so the file read is not hidden inside the panel read.
  SUPPORT_TICKET_INSPECTED: "SUPPORT_TICKET_INSPECTED",
  SUPPORT_TICKET_MATERIAL_VIEWED: "SUPPORT_TICKET_MATERIAL_VIEWED",
  // Reading another member's Ask Sage transcript. Audited for the same reason as
  // SUPPORT_TICKET_INSPECTED: it is one person reading another's words.
  ASK_SAGE_TRANSCRIPT_VIEWED: "ASK_SAGE_TRANSCRIPT_VIEWED",
  // The retention window blanking transcript text. Recorded because it is a
  // deletion of records an auditor may have expected to find.
  ASK_SAGE_TRANSCRIPTS_PURGED: "ASK_SAGE_TRANSCRIPTS_PURGED",
  // Platform-wide VaultSage sampling parameters. Audited because changing them
  // changes every review and Ask Sage answer on the platform.
  LLM_SETTINGS_UPDATED: "LLM_SETTINGS_UPDATED",
} as const;
export const auditActionSchema = z.enum([
  AuditAction.ORGANIZATION_CREATED,
  AuditAction.ORGANIZATION_UPDATED,
  AuditAction.ORGANIZATION_ARCHIVED,
  AuditAction.ORGANIZATION_SELECTED,
  AuditAction.VAULTSAGE_CREDENTIAL_CONFIGURED,
  AuditAction.VAULTSAGE_CREDENTIAL_ROTATED,
  AuditAction.VAULTSAGE_CREDENTIAL_REJECTED,
  AuditAction.VAULTSAGE_STORAGE_VERIFIED,
  AuditAction.VAULTSAGE_ORPHAN_STORAGE_DELETED,
  AuditAction.LOGIN,
  AuditAction.LOGOUT,
  AuditAction.USER_PROFILE_UPDATED,
  AuditAction.USER_PLATFORM_ROLE_CHANGED,
  AuditAction.MEMBER_INVITED,
  AuditAction.MEMBER_ACTIVATED,
  AuditAction.MEMBER_NAME_UPDATED,
  AuditAction.MEMBER_DEPARTMENT_UPDATED,
  AuditAction.MEMBER_ROLE_CHANGED,
  AuditAction.MEMBER_DISABLED,
  AuditAction.MEMBER_DELETED,
  AuditAction.POLICY_CREATED,
  AuditAction.POLICY_RENAMED,
  AuditAction.POLICY_VERSION_UPLOADED,
  AuditAction.POLICY_ACTIVATED,
  AuditAction.POLICY_RETIRED,
  AuditAction.POLICY_ARCHIVED,
  AuditAction.POLICY_UNARCHIVED,
  AuditAction.POLICY_DELETED,
  AuditAction.POLICY_VERSION_DOWNLOADED,
  AuditAction.POLICY_VERSION_REPROCESSED,
  AuditAction.POLICY_SCOPE_UPDATED,
  AuditAction.RULE_CREATED,
  AuditAction.RULE_VERSION_CREATED,
  AuditAction.RULE_ACTIVATED,
  AuditAction.RULE_RETIRED,
  AuditAction.RULE_DELETED,
  AuditAction.RULE_REFERENCE_UPLOADED,
  AuditAction.RULE_REFERENCE_DELETED,
  AuditAction.RULE_CHANNEL_UPDATED,
  AuditAction.RULE_SCOPE_UPDATED,
  AuditAction.LESSON_SUMMARY_UPDATED,
  AuditAction.CHANNEL_CREATED,
  AuditAction.CHANNEL_UPDATED,
  AuditAction.CHANNEL_DELETED,
  AuditAction.CATEGORY_CREATED,
  AuditAction.CATEGORY_UPDATED,
  AuditAction.CATEGORY_DELETED,
  AuditAction.CATEGORY_TEMPLATE_APPLIED,
  AuditAction.DICTIONARY_ENTRY_CREATED,
  AuditAction.DICTIONARY_ENTRY_UPDATED,
  AuditAction.DICTIONARY_ENTRY_DELETED,
  AuditAction.DICTIONARY_IMPORTED,
  AuditAction.SYNONYM_GROUP_CREATED,
  AuditAction.SYNONYM_GROUP_UPDATED,
  AuditAction.SYNONYM_GROUP_DELETED,
  AuditAction.BASELINE_GUIDE_APPLIED,
  AuditAction.CONFIG_UPDATED,
  AuditAction.REVIEW_CREATED,
  AuditAction.REVIEW_SUBMITTED,
  AuditAction.REVIEW_DELETED,
  AuditAction.REVIEW_ANALYZED,
  AuditAction.REVIEW_RESUBMITTED,
  AuditAction.REVIEW_RERUN,
  AuditAction.REVIEW_ASSIGNED,
  AuditAction.FINDING_NOTE_UPDATED,
  AuditAction.FINDING_DISPOSITION_SET,
  AuditAction.FINDING_LOCATION_UPDATED,
  AuditAction.MISSED_FINDING_RECORDED,
  AuditAction.DECISION_RECORDED,
  AuditAction.REPORT_DOWNLOADED,
  AuditAction.APPROVED_DOCUMENT_DOWNLOADED,
  AuditAction.SUPPORT_TICKET_CREATED,
  AuditAction.SUPPORT_TICKET_STATUS_CHANGED,
  AuditAction.SUPPORT_TICKET_REPLIED,
  AuditAction.SUPPORT_TICKET_INSPECTED,
  AuditAction.SUPPORT_TICKET_MATERIAL_VIEWED,
  AuditAction.ASK_SAGE_TRANSCRIPT_VIEWED,
  AuditAction.ASK_SAGE_TRANSCRIPTS_PURGED,
  AuditAction.LLM_SETTINGS_UPDATED,
]);
export type AuditAction = z.infer<typeof auditActionSchema>;
